// Ubicación en el repo: src/lib/patients.js
// Queries de la pantalla Pacientes. El filtrado de seguridad real (quién
// puede ver/editar qué paciente) lo hace RLS sobre `patients` — acá solo
// se agregan filtros de UX (sucursal activa elegida, texto de búsqueda) y
// se leen los conteos ya resueltos por la vista `patient_list_stats`
// (ver 0006_patient_list_stats_view.sql).
import { supabase } from './supabaseClient';

// Búsqueda liviana para el selector de paciente del modal de cita (spec
// 2.3 — "permite crear uno nuevo con solo nombre/apellido al vuelo").
export async function searchPatients(term) {
  const clean = term?.trim().replace(/[%,]/g, '');
  if (!clean) return [];
  const { data, error } = await supabase
    .from('patients')
    .select('id, nombre, segundo_nombre, apellido, segundo_apellido, rut')
    .is('deleted_at', null)
    .or(`nombre.ilike.%${clean}%,apellido.ilike.%${clean}%,rut.ilike.%${clean}%`)
    .limit(8);
  if (error) throw error;
  return data ?? [];
}

// Creación mínima desde la agenda (spec 2.3): solo nombre/apellido +
// sucursal activa, sin pasar por el formulario completo.
//
// OJO (ver DECISIONS.md #26): el id se genera acá mismo y el insert NO
// encadena .select() — patients_select exige patient_branch_match(id), que
// da false hasta que exista la fila en patient_branches, así que pedir la
// fila de vuelta (RETURNING) en el mismo insert rompe el alta para
// cualquier profesional no-Admin. Por eso: insertamos sin pedir de vuelta,
// recién después vinculamos la sucursal, y devolvemos un objeto armado a
// mano (alcanza para lo que usa el modal de cita).
export async function createQuickPatient(nombre, apellido, branchId, actorId) {
  const id = crypto.randomUUID();
  const upperNombre = nombre.toUpperCase();
  const upperApellido = apellido.toUpperCase();
  const { error } = await supabase
    .from('patients')
    .insert({ id, nombre: upperNombre, apellido: upperApellido, created_by: actorId, updated_by: actorId });
  if (error) throw error;
  if (branchId) {
    await supabase.from('patient_branches').insert({ patient_id: id, branch_id: branchId });
  }
  return { id, nombre: upperNombre, apellido: upperApellido, segundo_nombre: null, segundo_apellido: null };
}

// Paginado (DECISIONS.md, 23-ago-2026): `page` es 1-based, `pageSize` fijo
// en 25 desde Patients.jsx. Orden por `created_at` ascendente (más antiguo
// primero, pedido explícito de Nicolás) — mismo criterio con o sin
// búsqueda activa, para que el orden nunca cambie según lo que estés
// escribiendo. `count: 'exact'` trae el total real (no solo la página
// actual) para poder calcular cuántas páginas hay.
export async function listPatients({ branchId, search, page = 1, pageSize = 25 }) {
  let query = supabase
    .from('patient_list_stats')
    .select('*', { count: 'exact' })
    .is('deleted_at', null)
    .order('created_at', { ascending: true });

  if (branchId) {
    query = query.contains('branch_ids', [branchId]);
  }

  const term = search?.trim();
  if (term) {
    const escaped = term.replace(/[%,]/g, '');
    query = query.or(
      `nombre.ilike.%${escaped}%,apellido.ilike.%${escaped}%,segundo_nombre.ilike.%${escaped}%,segundo_apellido.ilike.%${escaped}%,rut.ilike.%${escaped}%`
    );
  }

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;
  if (error) throw error;
  return { data: data ?? [], count: count ?? 0 };
}

// Espeja patient_has_history() del backend (0005) para decidir en el
// cliente qué flujo de confirmación mostrar ANTES de intentar el update.
// La seguridad real sigue siendo el trigger server-side — esto es solo
// para no mostrarle a un no-Admin un formulario de eliminación que el
// backend igual va a rechazar.
export async function patientHasHistory(patientId) {
  const { data, error } = await supabase.rpc('patient_has_history', {
    p_patient_id: patientId,
  });
  if (error) throw error;
  return data === true;
}

export async function softDeletePatient(patientId, deletedByProfessionalId) {
  const { error } = await supabase
    .from('patients')
    .update({ deleted_at: new Date().toISOString(), deleted_by: deletedByProfessionalId })
    .eq('id', patientId);
  if (error) throw error;
}

// Papelera de pacientes (Configuración → Papelera, DECISIONS.md #75) — solo
// Admin llega a ver algo acá: `patients_select` (0003) ya filtra
// `deleted_at is null or is_admin()`, así que para cualquier otro rol esto
// devuelve simplemente una lista vacía, no un error.
export async function listDeletedPatients() {
  const { data, error } = await supabase
    .from('patients')
    .select('id, nombre, segundo_nombre, apellido, segundo_apellido, rut, deleted_at, deleted_by, professionals!deleted_by(nombre, apellido)')
    .not('deleted_at', 'is', null)
    .order('deleted_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

// Restaura SOLO la ficha (`patients.deleted_at`) — a propósito no toca sus
// citas/exámenes/etc., que quedaron cascada-eliminadas por el trigger de la
// migración 0032 al momento de borrar al paciente. Nicolás lo pidió así
// explícitamente: "restaurar sin incorporar sus horas antiguas".
export async function restorePatient(patientId) {
  const { error } = await supabase
    .from('patients')
    .update({ deleted_at: null, deleted_by: null })
    .eq('id', patientId);
  if (error) throw error;
}

// Elimina la ficha para siempre — solo funciona si no tiene ningún
// historial clínico (ver `permanently_delete_patient()`, migración 0032).
// Con historial, el backend rechaza la llamada y esto lanza el mensaje de
// error que devuelve el RPC.
export async function permanentlyDeletePatient(patientId) {
  const { error } = await supabase.rpc('permanently_delete_patient', { p_patient_id: patientId });
  if (error) throw new Error(error.message || 'No se pudo eliminar la ficha definitivamente.');
}

// "Forzar eliminación definitiva" (migración 0047, 25-ago-2026, pedido de
// Nicolás) — para fichas de PRUEBA que quedaron atascadas en la papelera
// por tener historial clínico asociado (permanentlyDeletePatient las
// rechaza siempre, sin excepción). Pasa por encima de ese chequeo, pero
// solo si quien llama es Admin, la ficha ya está en la papelera, y se
// entrega un motivo — todo lo que se borra queda registrado en
// `audit_log` con ese motivo. La verificación de contraseña (paso previo,
// ver ForceDeletePatientModal.jsx) es aparte, vía verifyCurrentPassword()
// en lib/auth.js.
export async function forceDeletePatientWithReason(patientId, reason) {
  const { error } = await supabase.rpc('force_delete_patient_with_reason', {
    p_patient_id: patientId,
    p_reason: reason,
  });
  if (error) throw new Error(error.message || 'No se pudo forzar la eliminación definitiva.');
}

// Ficha completa para el modal de edición (patient_list_stats no trae
// todos los campos del formulario, solo lo que necesita el listado).
export async function getPatientById(patientId) {
  const { data: patient, error } = await supabase
    .from('patients')
    .select('*')
    .eq('id', patientId)
    .single();
  if (error) throw error;

  const { data: branches, error: branchesError } = await supabase
    .from('patient_branches')
    .select('branch_id')
    .eq('patient_id', patientId);
  if (branchesError) throw branchesError;

  return { ...patient, branch_ids: (branches ?? []).map((b) => b.branch_id) };
}

// Chequeo amigable ANTES de guardar: si ya existe un paciente activo con
// este RUT, se le muestra su nombre al usuario para que no adivine. La
// protección real contra condiciones de carrera es el índice único
// `patients_rut_active_uidx` (0008) — esto es solo UX.
export async function findPatientByRut(sanitizedRut, excludePatientId) {
  if (!sanitizedRut) return null;
  let query = supabase
    .from('patients')
    .select('id, nombre, apellido')
    .eq('rut', sanitizedRut)
    .is('deleted_at', null)
    .limit(1);
  if (excludePatientId) query = query.neq('id', excludePatientId);
  const { data, error } = await query;
  if (error) throw error;
  return data?.[0] ?? null;
}

export async function sanitizeRut(rut) {
  if (!rut?.trim()) return null;
  const { data, error } = await supabase.rpc('sanitize_rut', { p_rut: rut });
  if (error) throw error;
  return data;
}

// `values` trae los campos del formulario (sin branch_ids); `branchIds` se
// guarda aparte en patient_branches. `actorId` = professional.id de quien
// guarda, para created_by/updated_by.
export async function createPatient(values, branchIds, actorId) {
  // Mismo motivo que createQuickPatient de arriba (DECISIONS.md #26): sin
  // .select() en el insert, para no pedirle a Postgres que valide
  // patient_branch_match(id) contra un paciente que todavía no tiene
  // ninguna sucursal vinculada.
  const id = crypto.randomUUID();
  const { error } = await supabase
    .from('patients')
    .insert({ id, ...values, created_by: actorId, updated_by: actorId });
  if (error) throw error;
  await savePatientBranches(id, branchIds);
  return { id, ...values };
}

export async function updatePatient(patientId, values, branchIds, actorId) {
  const { error } = await supabase
    .from('patients')
    .update({ ...values, updated_by: actorId })
    .eq('id', patientId);
  if (error) throw error;
  await savePatientBranches(patientId, branchIds);
}

// Ficha de detalle (spec-pacientes-agenda.md 1.4): datos generales +
// últimas citas + historial clínico resumido. Los adjuntos quedan afuera
// a propósito (DECISIONS.md #20 — bucket de Storage todavía sin
// configurar).
export async function getPatientDetail(patientId) {
  const [
    patientResult,
    branchesResult,
    appointmentsResult,
    examsResult,
    prescriptionsResult,
    hearingaidsResult,
    oftsalesResult,
  ] = await Promise.all([
    supabase.from('patients').select('*').eq('id', patientId).single(),
    // `logo_path` sumado acá (además de `name`, que ya se usaba para el
    // texto "Sucursal(es): ..."): al imprimir una audiometría/receta desde
    // el modal "Ver" de esta misma ficha (DECISIONS.md #41),
    // getPatientBranchLogoUrl() necesita `patient.patient_branches` con
    // esta misma forma para poder resolver el logo — se arma más abajo.
    supabase
      .from('patient_branches')
      .select('branch_id, branches(name, logo_path)')
      .eq('patient_id', patientId),
    supabase
      .from('appointments')
      // `appointments` tiene varias FK hacia professionals (professional_id,
      // created_by, updated_by, deleted_by) — hay que decir explícitamente
      // cuál seguir para el embed, si no PostgREST tira "more than one
      // relationship was found" por ambigüedad.
      .select('id, date, time, type, status, module, professionals!professional_id(nombre, apellido)')
      .eq('patient_id', patientId)
      .is('deleted_at', null)
      .order('date', { ascending: false })
      .order('time', { ascending: false })
      .limit(5),
    // `*` en vez de la lista chica de campos que traía antes: la ficha
    // ahora abre el mismo modal "Ver audiograma"/"Ver receta" que usan
    // Audiometrías/Recetas al hacer clic en un ítem del resumen (ver
    // DECISIONS.md #41), y esos modales necesitan todos los campos
    // clínicos (od/oi/bone_*/ldl_*/etc., lejos/cerca/av/etc.), no solo los
    // que se mostraban en la lista.
    supabase
      .from('exams')
      .select('*, professionals!professional_id(nombre, apellido, role, rut, signature_path)')
      .eq('patient_id', patientId)
      .is('deleted_at', null)
      .order('date', { ascending: false })
      .limit(10),
    supabase
      .from('prescriptions')
      .select('*, professionals!professional_id(nombre, apellido, role, rut, signature_path)')
      .eq('patient_id', patientId)
      .is('deleted_at', null)
      .order('date', { ascending: false })
      .limit(10),
    supabase
      .from('hearingaids')
      .select('id, purchase_date, brand, model, side, price')
      .eq('patient_id', patientId)
      .is('deleted_at', null)
      .order('purchase_date', { ascending: false })
      .limit(10),
    supabase
      .from('oftsales')
      .select('id, date, compro, price')
      .eq('patient_id', patientId)
      .is('deleted_at', null)
      .order('date', { ascending: false })
      .limit(10),
  ]);

  if (patientResult.error) throw patientResult.error;
  if (branchesResult.error) throw branchesResult.error;
  if (appointmentsResult.error) throw appointmentsResult.error;
  if (examsResult.error) throw examsResult.error;
  if (prescriptionsResult.error) throw prescriptionsResult.error;
  if (hearingaidsResult.error) throw hearingaidsResult.error;
  if (oftsalesResult.error) throw oftsalesResult.error;

  return {
    patient: {
      ...patientResult.data,
      branch_ids: (branchesResult.data ?? []).map((b) => b.branch_id),
      // Misma forma que `patients.patient_branches` en EXAM_LIST_SELECT/
      // PRESCRIPTION_LIST_SELECT (src/lib/exams.js, src/lib/prescriptions.js)
      // — permite reusar getPatientBranchLogoUrl() sin duplicar lógica.
      patient_branches: branchesResult.data ?? [],
    },
    branchNames: (branchesResult.data ?? []).map((b) => b.branches?.name).filter(Boolean),
    appointments: appointmentsResult.data ?? [],
    exams: examsResult.data ?? [],
    prescriptions: prescriptionsResult.data ?? [],
    hearingaids: hearingaidsResult.data ?? [],
    oftsales: oftsalesResult.data ?? [],
  };
}

async function savePatientBranches(patientId, branchIds) {
  const { error: deleteError } = await supabase
    .from('patient_branches')
    .delete()
    .eq('patient_id', patientId);
  if (deleteError) throw deleteError;

  if (!branchIds?.length) return;
  const { error: insertError } = await supabase
    .from('patient_branches')
    .insert(branchIds.map((branchId) => ({ patient_id: patientId, branch_id: branchId })));
  if (insertError) throw insertError;
}
